import { bundledQuran } from './source';
import type { SurahCoverage } from './trackProgress';

/**
 * One assignment in a school curriculum: a whole surah, or a span of it.
 *
 * A term assigns البقرة 1-10, not 286 ayahs, so a unit that could only name a
 * whole surah would measure the pupil against work nobody set them — 3% for
 * an assignment they had finished.
 */
export type CurriculumUnit = {
  surah: number;
  /** Both ends, or neither. A half-open unit is refused rather than defaulted. */
  from?: number;
  to?: number;
};

const lengthOf = (surah: number): number => {
  const meta = bundledQuran.surah(surah);
  return meta ? meta.ayahCount : 0;
};

/** The unit's span, clamped to the surah it names. Zero if the surah is not real. */
export function unitAyahs(unit: CurriculumUnit): number {
  const total = lengthOf(unit.surah);
  if (total === 0) return 0;
  if (unit.from === undefined || unit.to === undefined) return total;
  const from = Math.max(1, unit.from);
  const to = Math.min(total, unit.to);
  return to < from ? 0 : to - from + 1;
}

/** The unit's bounds as a closed range, or null when the surah is not real. */
export function boundsOf(unit: CurriculumUnit): [number, number] | null {
  const total = lengthOf(unit.surah);
  if (total === 0) return null;
  const from = unit.from === undefined ? 1 : Math.max(1, unit.from);
  const to = unit.to === undefined ? total : Math.min(total, unit.to);
  return to < from ? null : [from, to];
}

/**
 * How much of ONE unit a student has recited.
 *
 * Counts the overlap between their coverage and the unit's own span — so
 * reciting البقرة 50-60 completes none of an assignment of 1-10, however much
 * work it was, and reciting 1-20 completes all of it. Overlapping ranges count
 * once, for the same reason ayahsIn counts the union.
 */
export function unitProgress(
  coverage: Record<string, SurahCoverage>,
  unit: CurriculumUnit
): { covered: number; total: number; percent: number } {
  const bounds = boundsOf(unit);
  const total = unitAyahs(unit);
  if (!bounds || total === 0) return { covered: 0, total, percent: 0 };

  const [start, end] = bounds;
  const held = coverage[String(unit.surah)];
  const clipped = (held?.ranges ?? [])
    .filter((r) => r && Number.isFinite(r.from) && Number.isFinite(r.to))
    .map((r) => ({ from: Math.max(r.from, start), to: Math.min(r.to, end) }))
    .filter((r) => r.to >= r.from)
    .sort((x, y) => x.from - y.from);

  let count = 0;
  let reached = start - 1;
  for (const { from, to } of clipped) {
    if (to <= reached) continue;
    count += to - Math.max(from - 1, reached);
    reached = to;
  }
  const capped = Math.min(count, total);
  return { covered: capped, total, percent: Math.round((capped / total) * 100) };
}

/**
 * A whole term's progress, weighted by the real size of each unit.
 *
 * Weighted, not averaged over units: الفاتحة complete out of الفاتحة plus
 * البقرة 1-10 is 7 of 17 ayahs, and calling that half done because one unit of
 * two is finished would flatter every pupil with a short surah.
 *
 * A unit naming a surah that does not exist contributes to neither side of the
 * fraction — one bad row in a curriculum must not make every pupil's
 * percentage wrong.
 */
export function curriculumProgress(
  coverage: Record<string, SurahCoverage>,
  units: CurriculumUnit[]
): { covered: number; total: number; percent: number; done: number; units: number } {
  let covered = 0;
  let total = 0;
  let done = 0;
  let counted = 0;
  for (const unit of units) {
    const size = unitAyahs(unit);
    if (size === 0) continue;
    counted += 1;
    const one = unitProgress(coverage, unit);
    covered += one.covered;
    total += size;
    if (one.covered >= size) done += 1;
  }
  return {
    covered,
    total,
    percent: total === 0 ? 0 : Math.round((covered / total) * 100),
    done,
    units: counted,
  };
}

/**
 * Whether a unit is one an admin may save.
 *
 * A range past the end of its surah is REFUSED rather than clamped: an admin
 * who typed three ayahs too many should be told, not silently corrected into
 * a curriculum that does not say what they wrote.
 */
/**
 * The next thing this pupil has to recite — م-2-04's "بترتيب المنهج لا
 * بترتيب المصحف".
 *
 * The FIRST unfinished unit in the order the school set, which is why this
 * walks the array rather than sorting it: a curriculum may set الناس before
 * الفاتحة, and a "next" chosen by surah number would send the pupil to work
 * nobody asked them for. Null when the term is done — a home that invents
 * one sends them back over finished work.
 */
export function nextUnit(
  coverage: Record<string, SurahCoverage>,
  units: CurriculumUnit[],
): CurriculumUnit | null {
  for (const unit of units) {
    const total = unitAyahs(unit);
    if (total === 0) continue;
    if (unitProgress(coverage, unit).covered < total) return unit;
  }
  return null;
}

/**
 * Where a pupil picks a unit up: the first ayah of its span that no recorded
 * coverage holds. A finished unit (or an unreal one) starts again at its first
 * ayah — "continue from" must always name an ayah that exists.
 */
export function firstUncoveredAyah(
  coverage: Record<string, SurahCoverage>,
  unit: CurriculumUnit,
): number {
  const bounds = boundsOf(unit);
  if (!bounds) return 1;
  const [start, end] = bounds;
  const ranges = (coverage[String(unit.surah)]?.ranges ?? [])
    .filter((r) => r && Number.isFinite(r.from) && Number.isFinite(r.to));
  for (let ayah = start; ayah <= end; ayah += 1) {
    if (!ranges.some((r) => r.from <= ayah && ayah <= r.to)) return ayah;
  }
  return start;
}

export function validUnit(unit: CurriculumUnit): boolean {
  const total = lengthOf(unit.surah);
  if (total === 0) return false;
  const hasFrom = unit.from !== undefined;
  const hasTo = unit.to !== undefined;
  if (hasFrom !== hasTo) return false;
  if (!hasFrom) return true;
  const from = unit.from as number;
  const to = unit.to as number;
  if (!Number.isInteger(from) || !Number.isInteger(to)) return false;
  return from >= 1 && to <= total && from <= to;
}
